import { useState, useEffect, useContext } from 'react';
import { AppConfigContext } from "../context/AppConfigContext";
import { CloudContext } from "../context/CloudContext";
import ProjectDashboard from "../components/ProjectDashboard.jsx"
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import SideNav from '../layout/SideNav';
import { TopNav } from '../layout/TopNav';
import { styled } from '@mui/material/styles';
import { useTranslation } from "react-i18next";

import NewProjectDialog from "../dialog/NewProjectDialog.jsx"
import NewDataDialog from "../dialog/NewDataDialog.jsx"
import EditProjectDialog from "../dialog/EditProjectDialog.jsx"
import DeleteProjectDialog from "../dialog/DeleteProjectDialog.jsx"

import UploadQueue from '../components/UploadQueue';

import { useAuth } from '../hooks/useAuth';
import { useProjects } from '../hooks/useProjects';
import { useCredentials } from '../hooks/useCredentials';

//import {MappingViewer} from './MappingViewer.jsx'

const SIDE_NAV_WIDTH = 280;

/**
 * Main application component that handles the primary dashboard interface
 * Manages project display, navigation, and user interaction for authenticated users
 */
function Main() {
    // Application configuration state from context
    const { 
        usage,              // Current storage usage in GB
        maxSpace,           // Maximum allowed storage space
        setUsage,           // Function to update usage
        setMaxLinkCesium,   // Function to update max cesium links
        setUsedCesiumLinks  // Function to update used cesium links
    } = useContext(AppConfigContext);
    
    // Cloud service credentials from context
    const {
        region,              // AWS region
        idpool,              // AWS identity pool ID
        cloudinary_cloud,    // Cloudinary cloud name
        cloudinary_preset    // Cloudinary upload preset
    } = useContext(CloudContext);

    // Local component state
    const [language, setLanguage] = useState("jp");
    const { t, i18n } = useTranslation();

    // Custom hooks for business logic separation
    const { user, userAuth, getStrapiUser, jwt, idToken, service, isLoading: authLoading } = useAuth();
    const {
        processings,         // Available data processing options
        getCredentials,      // Function to fetch cloud credentials
        retrieveProcessings  // Function to fetch processing configurations
    } = useCredentials();
    const {
        projects,            // User's projects array
        projectSelected,     // Currently selected project
        setProjectSelected,  // Function to change selected project
        userHasNoProject,    // Boolean indicating if user has any projects
        GoUploadedThisMonth, // Data uploaded this month in GB
        maxGoUploadPerMonth, // Maximum monthly upload limit
        loading: projectsLoading, // Loading state for project operations
        retrieveProjects     // Function to refresh projects from API
    } = useProjects(getStrapiUser, setUsage, service, getCredentials, retrieveProcessings, setMaxLinkCesium, setUsedCesiumLinks);

    const LayoutRoot = styled('div')(({ theme }) => ({
        display: 'flex',
        flex: '1 1 auto',
        maxWidth: '100%',
        [theme.breakpoints.up('lg')]: {
            paddingLeft: SIDE_NAV_WIDTH
        }
    }));

    const LayoutContainer = styled('div')({
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        width: '100%'
    });

    /**
     * Initialize component on mount and when user authentication changes
     * Restores user's language preference and loads their projects
     */
    useEffect(() => {
        // Restore saved language preference from localStorage
        const savedLanguage = localStorage.getItem('languageMirukuru');
        if (savedLanguage) {
            setLanguage(savedLanguage);
            i18n.changeLanguage(savedLanguage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Load user projects only when auth is fully loaded and both user and service are resolved
        if (!authLoading && user && service) {
            retrieveProjects();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, user, service]);

    // Dialog visibility state management
    const [openNav, setOpenNav] = useState(false);                    // Mobile navigation drawer
    const [openDialogProject, setOpenDialogProject] = useState(false); // New project dialog
    const [openDialogData, setOpenDialogData] = useState(false);       // Upload data dialog
    const [openDialogEditProject, setOpenDialogEditProject] = useState(false); // Edit project dialog
    const [openDialogDeleteProject, setOpenDialogDeleteProject] = useState(false); // Delete project dialog

    /**
     * Opens the new project creation dialog and closes navigation
     */
    const handleNewProject = () => {
        setOpenDialogProject(true);
        setOpenNav(false);
    };

    /**
     * Opens the project deletion confirmation dialog and closes navigation
     */
    const handleDeleteProject = () => {
        setOpenDialogDeleteProject(true);
        setOpenNav(false);
    };

    /**
     * Opens the project editing dialog and closes navigation
     */
    const handleEditProject = () => {
        setOpenDialogEditProject(true);
        setOpenNav(false);
    };

    /**
     * Opens the data upload dialog
     */
    const handleNewData = () => {
        setOpenDialogData(true);
    };

    /**
     * Callback function to refresh projects list after operations
     * Used by child components when they modify project data
     */
    const callBackGetProjects = () => {
        retrieveProjects();
    };

    return (
        <div className="Main">
            <TopNav onNavOpen={() => setOpenNav(true)} language={language} setLanguage={setLanguage} projects={projects} loading={projectsLoading} />
            <SideNav onClose={() => setOpenNav(false)} open={openNav} userHasNoProject={userHasNoProject} handleClickOnNewProject={handleNewProject} projects={projects} projectSelected={projectSelected} setProjectSelected={setProjectSelected} usage={usage} maxSpace={maxSpace} GoUploadedThisMonth={GoUploadedThisMonth} maxGoUploadPerMonth={maxGoUploadPerMonth}
            />
            {(!userHasNoProject) ?
                <>
                    <LayoutRoot>
                        <LayoutContainer>
                            <Box sx={{ flexGrow: 1, margin: 1 }}>
                                <ProjectDashboard
                                    projectSelected={projectSelected}
                                    handleClickOnNewData={handleNewData}
                                    handleClickOnEditProject={handleEditProject}
                                    handleClickOnDeleteProject={handleDeleteProject}
                                    reloadProjects={callBackGetProjects}
                                    processings={processings}
                                />
                            </Box>
                        </LayoutContainer>
                    </LayoutRoot>

                    <UploadQueue
                        user={user}
                        idToken={idToken}
                        jwt={jwt}
                        region={region}
                        idpool={idpool}
                        cloudinary_cloud={cloudinary_cloud}
                        cloudinary_preset={cloudinary_preset}
                        projectSelected={projectSelected}
                        reloadProjects={callBackGetProjects}
                        userId={userAuth.id}
                    >
                        {({ addToUploadQueue }) => (
                            <>
                                {/* Pass addToUploadQueue to NewDataDialog */}
                                <NewDataDialog
                                    open={openDialogData}
                                    setOpen={setOpenDialogData}
                                    userId={userAuth.id}
                                    projectSelected={projectSelected}
                                    reloadProjects={callBackGetProjects}
                                    addToUploadQueue={addToUploadQueue}
                                    GoUploadedThisMonth={GoUploadedThisMonth}
                                    maxGoUploadPerMonth={maxGoUploadPerMonth}
                                    processings={processings}
                                />
                            </>
                        )}
                    </UploadQueue>
                    <EditProjectDialog open={openDialogEditProject} setOpen={setOpenDialogEditProject} projectSelected={projectSelected} reloadProjects={callBackGetProjects} />
                    <DeleteProjectDialog open={openDialogDeleteProject} setOpen={setOpenDialogDeleteProject} projectSelected={projectSelected} reloadProjects={callBackGetProjects} />
                </> :
                <>
                    {!projectsLoading &&
                        <LayoutRoot>
                            <LayoutContainer>
                                <Box sx={{ flexGrow: 1, padding: 2 }}>
                                    <Stack spacing={3} sx={{ alignItems: 'center' }}>
                                        <Typography variant="h6">
                                            {t("Welcome")}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("PleaseStart")}
                                        </Typography>
                                    </Stack>
                                </Box>
                            </LayoutContainer>
                        </LayoutRoot>
                    }
                </>
            }
            <NewProjectDialog open={openDialogProject} setOpen={setOpenDialogProject} userId={userAuth.id} handleNewProjectCreated={callBackGetProjects} />
        </div>
    );
}

export default Main;
